import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaDownload, FaEye } from "react-icons/fa";
import styles from "./styles.module.css";
import { CustomButton, CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useSelector } from "react-redux";

const ListFeeType = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  // Fallback academic year id, used only when the user hasn't picked one in the dropdown.
  const tokenAcademicId =
    clientData?.academic_id || clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  // ── Academic Year filter dropdown (from API) ──
  const [academicYear, setAcademicYear] = useState("");
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  // Gate the list fetch until the academic years are loaded and the default
  // active year is resolved — prevents the list API from firing twice on mount.
  const [academicYearResolved, setAcademicYearResolved] = useState(false);
  const academicIdToSend = academicYear || tokenAcademicId;

  useEffect(() => {
    fetchAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id]);

  /** Academic Year list API: GET school/academic/{school_id} */
  const fetchAcademicYears = async () => {
    if (!school_id) return;
    setAcademicYearLoading(true);
    try {
      const response = await AxiosInstance.get(`/school/academic/${school_id}`);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        const list = response.data.data;
        const options = list
          .filter((item) => item.isActive === 1 || item.isUpcoming === 1)
          .map((item) => ({
            value: String(item.id),
            label: item.academic_year,
          }));
        setAcademicYearOptions(options);

        // Default-select the active academic year (isActive === 1) if the
        // user hasn't already picked one.
        const activeYear = list.find((item) => item.isActive === 1);
        if (activeYear) {
          setAcademicYear((prev) => prev || String(activeYear.id));
        }
      } else {
        setAcademicYearOptions([]);
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
      setAcademicYearOptions([]);
    } finally {
      setAcademicYearLoading(false);
      setAcademicYearResolved(true);
    }
  };

  const handleAcademicYearChange = (value) => {
    const finalValue =
      value && typeof value === "object" ? (value.value ?? "") : value;
    setAcademicYear(finalValue || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  useEffect(() => {
    if (school_id && academicYearResolved) {
      fetchFeeTypeList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    school_id,
    academicYearResolved,
    pagination.pageIndex,
    pagination.pageSize,
    apiSearch,
    academicIdToSend,
  ]);

  /** List API: schoolFees/list/fees_type (server-side paginated) */
  const fetchFeeTypeList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("schoolFees/list/fees_type", {
        school_id: school_id,
        academic_id: Number(academicIdToSend) || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: apiSearch.trim(),
      });

      if (response?.data?.status) {
        const list = response.data.data || [];
        const transformed = list.map((item, index) => ({
          s_no: pagination.pageIndex * pagination.pageSize + index + 1,
          id: item.id,
          name: item.name || "",
          code: item.code || "",
          academic_year: item.academic_year || "",
          description: item.description || "",
        }));
        setData(transformed);
        setRowCount(response.data.total_records || transformed.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching fee type list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAdd = () => {
    navigate("/user/createFeeType");
  };

  const handleView = (row) => {
    navigate("/user/createFeeType", {
      state: { ID: row.id, data: row },
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "s_no",
        header: translations.labels.serialNo,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "name",
        header: translations.labels.name,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "code",
        header: translations.labels.code,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      // {
      //     accessorKey: "academic_year",
      //     header: translations.labels?.academicYear || "Academic Year",
      //     enableSorting: true,
      //     enableColumnFilter: false,
      //     Cell: ({ cell }) => cell.getValue() || "-",
      // },
      {
        accessorKey: "description",
        header: translations.labels.description,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        id: "actions",
        header: translations.labels.actions,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translations],
  );

  /** Convert array of objects to CSV string */
  const convertToCSV = (rows, headerMap) => {
    if (!rows || rows.length === 0) return "";

    const keys = Object.keys(headerMap);
    const headerRow = keys.map((k) => `"${headerMap[k]}"`).join(",");

    const body = rows.map((row) =>
      keys
        .map((key) => {
          const val = row[key] ?? "";
          const escaped = String(val).replace(/"/g, '""');
          return `"${escaped}"`;
        })
        .join(","),
    );

    return [headerRow, ...body].join("\n");
  };

  /** Trigger CSV file download in the browser */
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /** Download all fee type records as CSV.
   * NOTE: passing empty page/limit makes the API return the full record set
   * in one response, so this must NOT loop over pages — the API's
   * total_pages is unreliable when page/limit are blank and looping caused
   * the same rows to be appended more than once. */
  const handleDownload = async () => {
    try {
      setDownloadLoading(true);

      const response = await AxiosInstance.post("schoolFees/list/fees_type", {
        school_id: school_id,
        academic_id: Number(academicIdToSend) || "",
        page: "",
        limit: "",
        search: apiSearch.trim(),
      });

      const allData = response?.data?.status ? response.data.data || [] : [];

      if (allData.length === 0) {
        setModalState({
          open: true,
          type: "error",
          message: "No data available to download.",
          redirectPath: "",
        });
        return;
      }

      const exportRows = allData.map((item, index) => ({
        s_no: index + 1,
        name: item.name || "",
        code: item.code || "",
        description: item.description || "",
      }));

      const csvContent = convertToCSV(exportRows, {
        s_no: "S.No",
        name: "Name",
        code: "Code",
        description: "Description",
      });

      downloadCSV(csvContent, "FEE_TYPE_LIST.csv");
    } catch (error) {
      console.error("Error downloading fee type list:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Failed to download fee type list. Please try again.",
        redirectPath: "",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={() => setModalState((prev) => ({ ...prev, open: false }))}
      />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>{translations.labels.feeType}</h1>
      </div>

      {/* Filters + Actions */}
      <div className={styles.actionsWithFilter}>
        <div className={styles.academicYearFilter}>
          <CustomDropdown
            label={translations.labels?.academicYear || "Academic Year"}
            name="academicYear"
            value={academicYear}
            onChange={handleAcademicYearChange}
            options={academicYearOptions}
            placeholder={
              academicYearLoading
                ? translations.labels?.loading || "Loading..."
                : translations.labels?.selectAcademicYear ||
                  "Select academic year"
            }
            isClearable={false}
            isDisabled={academicYearLoading}
          />
        </div>

        <div className={styles.actionButtonsGroup}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleAdd}
          >
            {translations.labels.add}
          </CustomButton>

          <CustomButton
            type="button"
            variant="contained"
            className={`${styles.downloadButton} `}
            fullWidth={false}
            onClick={handleDownload}
            disabled={downloadLoading}
          >
            <FaDownload style={{ marginRight: "8px" }} />
            {downloadLoading
              ? "Downloading..."
              : translations.labels?.downloadButton || "Download"}
          </CustomButton>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        placeholder={translations?.labels?.searchNameCode || "Search..."}
        setSearchInput={setSearchInput}
        onSearchEnter={handleSearchEnter}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        enableSearch={true}
        enableFiltering={false}
        manualPagination={true}
      />
    </div>
  );
};

export default ListFeeType;
